'use client';

import { useEffect, useRef } from 'react';
import { useWebSocketContext } from '@/shared/providers/WebSocketProvider';
import type { WSMessage } from '@/shared/lib/types';

interface UseWebSocketOptions {
  onProjectStatusUpdated?: (status: string) => void;
  /** Indexação em lote (fluxo do projeto) concluída com sucesso. */
  onIndexingComplete?: (projectId: string) => void;
  /** Indexação em lote (fluxo do projeto) falhou. */
  onIndexingError?: (projectId: string) => void;
  /** Um lote de indexação terminou (sucesso, falha ou cancelamento) — pode
   * haver vários por projeto antes de `onIndexingComplete`/`onIndexingError`. */
  onIndexingBatchComplete?: (projectId: string) => void;
  /** O socket voltou a ligar depois de uma queda. Tudo o que o backend tenha
   * publicado durante esse intervalo perdeu-se (entrega fire-and-forget, sem
   * replay), por isso quem depende de eventos WS para saber em que estado
   * está um projeto deve recarregá-lo aqui — é o que evita ficar preso "a
   * processar" quando a indexação termina com o socket em baixo. */
  onReconnected?: () => void;
}

// Hook fino sobre o WebSocketProvider: a ligação real (socket, reconexão,
// ping, atualização das stores globais) vive lá, montada uma única vez em
// AppShell. Aqui só nos registamos como subscritores dessa mensagem única,
// filtrada por `activeProjectId` quando aplicável.
export function useWebSocket(activeProjectId?: string, options?: UseWebSocketOptions) {
  const { isConnected, addListener, reconnectEpoch } = useWebSocketContext();

  // Refs para os callbacks/activeProjectId mais recentes: o listener é
  // registado uma vez (ver deps do useEffect abaixo) e não deve ficar preso a
  // uma versão antiga de `options` ou `activeProjectId` em re-renders seguintes.
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const activeProjectIdRef = useRef(activeProjectId);
  activeProjectIdRef.current = activeProjectId;

  useEffect(() => {
    return addListener((message: WSMessage) => {
      const isIndexingEvent = ['indexing_complete', 'indexing_error', 'indexing_batch_complete'].includes(
        message.type,
      );
      const projectId = activeProjectIdRef.current;
      const isForActiveProject = !projectId || !message.project_id || message.project_id === projectId;

      if (!isIndexingEvent && !isForActiveProject) {
        return;
      }

      switch (message.type) {
        case 'indexing_complete':
          if (message.project_id) {
            optionsRef.current?.onIndexingComplete?.(message.project_id);
          }
          break;

        case 'indexing_error':
          if (message.project_id) {
            optionsRef.current?.onIndexingError?.(message.project_id);
          }
          break;

        case 'indexing_batch_complete':
          if (message.project_id) {
            optionsRef.current?.onIndexingBatchComplete?.(message.project_id);
          }
          break;

        case 'project_status_updated':
          optionsRef.current?.onProjectStatusUpdated?.(message.data.status);
          break;
      }
    });
  }, [addListener]);

  // `reconnectEpoch` só muda em reconexões (nunca na ligação inicial, ver
  // WebSocketProvider), por isso não é preciso distinguir o primeiro render
  // aqui: quem monta este hook já faz o seu carregamento inicial.
  useEffect(() => {
    if (reconnectEpoch === 0) return;
    optionsRef.current?.onReconnected?.();
  }, [reconnectEpoch]);

  return { isConnected };
}
